/**
 * Utility functions for notes 
 * 
 * These utilities work with both database format (NoteData) and UI format (FormattedNote)
 */
import { NoteData, FormattedNote, NoteFilters } from './noteTypes';

/**
 * Type guard to check if a note is in FormattedNote format
 */
function isFormattedNote(note: NoteData | FormattedNote): note is FormattedNote {
  return 'topicId' in note && 'userId' in note && 'createdAt' in note;
}

/**
 * Apply filters to a list of notes (works with both NoteData and FormattedNote formats)
 * 
 * @param notes Array of notes to filter
 * @param filters Filter criteria to apply
 * @returns Filtered array of notes
 */
export function applyFilters<T extends NoteData | FormattedNote>(notes: T[], filters: NoteFilters): T[] {
  let filteredNotes = [...notes];
  
  // Apply search query filter
  if (filters.searchQuery) {
    const normalizedQuery = filters.searchQuery.toLowerCase().trim();
    filteredNotes = filteredNotes.filter(note => {
      // Check if we're dealing with FormattedNote or NoteData
      const content = isFormattedNote(note) ? note.content : note.content;
      const summary = isFormattedNote(note) ? note.summary : note.summary;
      
      // Search in content and summary
      const contentMatch = content.toLowerCase().includes(normalizedQuery);
      const summaryMatch = summary ? summary.toLowerCase().includes(normalizedQuery) : false;
      
      return contentMatch || summaryMatch;
    });
  }
  
  // Apply user filter
  if (filters.userIds && filters.userIds.length > 0) {
    filteredNotes = filteredNotes.filter(note => {
      const userId = isFormattedNote(note) ? note.userId : note.user_id;
      return filters.userIds?.includes(userId);
    });
  }
  
  return filteredNotes;
}

/**
 * Sorts notes by creation date in ascending order (oldest first)
 * Works with both NoteData and FormattedNote
 */
export function sortByCreationDateAsc<T extends NoteData | FormattedNote>(notes: T[]): T[] {
  return [...notes].sort((a, b) => {
    const dateA = isFormattedNote(a) ? new Date(a.createdAt) : new Date(a.created_at);
    const dateB = isFormattedNote(b) ? new Date(b.createdAt) : new Date(b.created_at);
    return dateA.getTime() - dateB.getTime();
  });
}

/**
 * Sorts notes by creation date in descending order (newest first)
 * Works with both NoteData and FormattedNote
 */
export function sortByCreationDateDesc<T extends NoteData | FormattedNote>(notes: T[]): T[] {
  return [...notes].sort((a, b) => {
    const dateA = isFormattedNote(a) ? new Date(a.createdAt) : new Date(a.created_at);
    const dateB = isFormattedNote(b) ? new Date(b.createdAt) : new Date(b.created_at);
    return dateB.getTime() - dateA.getTime();
  });
}